import { getTenantId, setTenantId } from '@innovyze/lib_get_service';
import {
	setDefaultSite,
	setDefaultSiteName,
	setTeams,
	setToken,
	setUser,
} from '../../../actions/authenticationContext.actions';
import { useProjects } from '../../../hooks/useProjects';
import {
	getAutodeskTeams,
	getAutodeskUserProfile,
	getAutodeskContext,
	getAutodeskContextByOrgId,
} from '../../../services/forge.service';
import {
	AutodeskContext,
	AutodeskTeams,
	AutodeskUserProfile,
} from '../../../types/forge';

import { useDispatch, useSelector } from 'react-redux';
import { setUserContext } from '../../../actions/userContext.actions';
import { SiteConfig } from '../../../contexts/SiteConfiguration';
import { selectApplicationName } from '../../../selectors';
import { useIsFeatureEnabled } from '../../../utils';
import { processUserContextToStore } from '../../../utils/UserContext';

export const useForgeUserContext = (config: SiteConfig | null) => {
	const dispatch = useDispatch();
	const { activeProject, projects } = useProjects();
	const appName = useSelector(selectApplicationName);
	const isProjectPickerEnabled = useIsFeatureEnabled(
		'info-360-platform-product-picker',
	);
	// Workaround for feature toggles not being loaded
	// TODO: Remove this once product picker toggle is removed
	const enteredFromProjectInjection = localStorage.getItem('projectFlow');
	const shouldUseProjectPicker =
		isProjectPickerEnabled || enteredFromProjectInjection;

	const getUserProfileData = async (accessToken?: string) => {
		const userProfileResponse = await getAutodeskUserProfile(
			config?.forge,
			accessToken,
		);
		return userProfileResponse;
	};

	const retrieveUserContext = async (
		accessToken: string,
		isLandingPage?: boolean,
	) => {
		// TODO pipe this through redux instead
		// NEW TODO this needs to be handled via forking logic, not serially
		const userProfile = await getUserProfileData(accessToken);
		if (!isLandingPage) {
			let autodeskUserContext, autodeskUserTeams;
			if (shouldUseProjectPicker && !!activeProject) {
				[autodeskUserContext, autodeskUserTeams] = await Promise.all([
					getAutodeskContext(
						appName,
						activeProject?.hubUrn || null,
						activeProject?.id || null,
					),
					getAutodeskTeams(),
				]);
			} else {
				[autodeskUserContext, autodeskUserTeams] = await Promise.all([
					getAutodeskContextByOrgId(appName, getTenantId() || null),
					getAutodeskTeams(),
				]);
			}
			return {
				autodeskUserContext,
				autodeskUserTeams,
				userProfile,
			};
		}

		return { userProfile };
	};

	const storeUserContext = (
		autodeskUserContext: AutodeskContext | null,
		autodeskUserTeams: AutodeskTeams | null,
		userProfile: AutodeskUserProfile,
		accessToken: string,
		isLandingPage?: boolean,
	) => {
		const { defaultSite, defaultSiteName, teams, userContext } =
			processUserContextToStore(
				{
					autodeskUserContext,
					autodeskUserTeams,
					autodeskUserProjects: projects,
					userProfile,
					accessToken,
				},
				isLandingPage,
				!!isProjectPickerEnabled,
			);
		dispatch(setToken(accessToken));
		dispatch(setUser(userProfile));
		dispatch(setUserContext(userContext));
		dispatch(setDefaultSite(!isLandingPage ? defaultSite : 'LandingPage'));
		if (!isLandingPage) {
			dispatch(setTeams(teams));
			dispatch(setDefaultSiteName(defaultSiteName));
			setTenantId(defaultSite);
		}
	};

	return { retrieveUserContext, storeUserContext };
};
